/**
 * Rescue Portal analytics — tracks user behavior via Segment.
 *
 * All events are prefixed with "Rescue Portal" and enriched with org context.
 * Impersonation sessions are automatically suppressed by the shared analytics wrapper.
 */

import analytics from '../lib/analytics';
import type { RescueDeviceOrderAttestationVariant } from '@/types/gql-op-types';

// ── Rescue org context (set once on portal load) ───────────────────

interface RescueOrgContext {
  rescue_org_id: string;
  rescue_org_name: string;
}

let orgCtx: RescueOrgContext = { rescue_org_id: '', rescue_org_name: '' };

export function setRescueAnalyticsContext(orgId: string, orgName: string) {
  orgCtx = { rescue_org_id: orgId, rescue_org_name: orgName };
}

function track(event: string, props: Record<string, unknown> = {}) {
  analytics.track(`Rescue Portal ${event}`, { ...orgCtx, ...props });
}

// ── Login ─────────────────────────────────────────────────────────

// Used to defer trackLogin() until RescueContextProvider has loaded org
// context, since RescueLogin is mounted outside the provider.
export const RESCUE_LOGIN_PENDING_TRACK_KEY = 'rescue_login_pending_track';

export type LoginFailureReason = 'invalid_credentials' | 'unknown_error';

export function trackLogin() {
  track('Login');
}

export function trackLoginFailed(reason: LoginFailureReason) {
  track('Login Failed', { reason });
}

// ── Page views ─────────────────────────────────────────────────────

export function trackRescuePageView(pagePath: string, pageTitle: string) {
  track('Page Viewed', { page_path: pagePath, page_title: pageTitle });
}

// ── Dashboard ──────────────────────────────────────────────────────

export function trackDashboardViewed(actionItemCount: number, t2aRate: number) {
  track('Dashboard Viewed', { action_item_count: actionItemCount, t2a_rate: t2aRate });
}

export function trackActionItemClicked(actionItemType: string, urgency: string) {
  track('Action Item Clicked', { action_item_type: actionItemType, urgency });
}

// ── Chips ──────────────────────────────────────────────────────────

export function trackChipRegistrationStarted(entryPoint: string) {
  track('Chip Registration Started', { entry_point: entryPoint });
}

export function trackChipRegistered(hasOwner: boolean, species?: string) {
  track('Chip Registered', { has_owner: hasOwner, species });
}

export function trackChipOwnerAdded(entryPoint?: string) {
  track('Chip Owner Added', { entry_point: entryPoint });
}

export function trackChipRemoveFromPetClicked() {
  track('Chip Remove From Pet Clicked');
}

export function trackChipRemovedFromPet() {
  track('Chip Removed From Pet');
}

export function trackChipsOrderClicked() {
  track('Chips Order Clicked');
}

export function trackChipsOrderSubmitted(quantity: number) {
  track('Chips Order Submitted', { quantity });
}

export function trackChipConditionUpdated(condition: string) {
  track('Update Chip Condition', { condition });
}

export function trackPetLookupSearched(found: boolean) {
  track('Pet Lookup Searched', { found });
}

// ── Devices ────────────────────────────────────────────────────────

/**
 * `fosterSource` separates a placement that reused a saved home from one typed in fresh — the
 * ratio is what says whether the roster is earning its keep. `saveToRoster` is the rescue's
 * opt-in on a new home; it is always true for a saved one, which the backend ignores.
 */
export function trackDeviceTransferToFoster(
  entryPoint?: string,
  opts?: { fosterSource?: FosterSource; saveToRoster?: boolean },
) {
  track('Device Transfer to Foster Initiated', {
    entry_point: entryPoint,
    foster_source: opts?.fosterSource,
    save_to_roster: opts?.saveToRoster,
  });
}

export function trackDeviceTransferToAdopt(entryPoint?: string) {
  track('Device Transfer to Adopt Initiated', { entry_point: entryPoint });
}

/**
 * `saveToRoster` is the recall modal's opt-in checkbox — false when it was left unticked, when the
 * home was already saved (the option is hidden), or when the flow never offers it (foster-adopt).
 */
export function trackDeviceRecalled(entryPoint?: string, opts?: { saveToRoster?: boolean }) {
  track('Device Recalled', { entry_point: entryPoint, save_to_roster: opts?.saveToRoster });
}

export function trackDeviceResendEmail() {
  track('Device Resend Email');
}

export function trackDeviceCancelTransfer(entryPoint?: string, reason?: string) {
  track('Device Cancel Transfer', { entry_point: entryPoint, reason });
}

// Fired when a cancel-reason flow fails at the confirmation step. Dimensioned by
// the same `reason` as the success event so failure rate per reason is a direct
// ratio. `reason` is a stable slug (CancellationReason), never a raw error.
export function trackDeviceCancelTransferFailed(entryPoint?: string, reason?: string) {
  track('Device Cancel Transfer Failed', { entry_point: entryPoint, reason });
}

export function trackDeviceMarkedRecovered(entryPoint?: string) {
  track('Device Marked Recovered', { entry_point: entryPoint });
}

export function trackDeviceMarkedNotRecovered(entryPoint?: string) {
  track('Device Marked Not Recovered', { entry_point: entryPoint });
}

// Fired when a pending adoption is flagged stalled (the new mark-stalled
// endpoint). Kept distinct from Cancel Transfer because it's a real state
// transition — the transfer stays PENDING, it does not cancel.
export function trackDeviceMarkedStalled(entryPoint?: string) {
  track('Device Marked Stalled', { entry_point: entryPoint });
}

export function trackDeviceSmsSent() {
  track('Device SMS Sent');
}

export function trackDevicesOrderClicked(variant?: RescueDeviceOrderAttestationVariant) {
  track('Devices Order Clicked', { attestation_variant: variant });
}

// The device-order funnel. Splitting the attestation into its own step is what makes
// `Devices Order Details Completed` → `Devices Attestation Viewed` → `Devices Attestation Agreed`
// three distinct numbers: inline, a rescue that balks at the terms is indistinguishable from one who
// abandoned the address fields. Every step carries `attestation_variant` so a first order and a
// recurring one can be compared.
/** Step 1 validated; the rescue is on their way to the terms. */
export function trackDevicesOrderDetailsCompleted(variant: RescueDeviceOrderAttestationVariant, deviceCount: number) {
  track('Devices Order Details Completed', { attestation_variant: variant, device_count: deviceCount });
}

/** Step 2 rendered — the denominator for terms refusal. */
export function trackDevicesAttestationViewed(variant: RescueDeviceOrderAttestationVariant) {
  track('Devices Attestation Viewed', { attestation_variant: variant });
}

/** The agreement checkbox was ticked. Fired on agreement, not on submit success. */
export function trackDevicesAttestationAgreed(variant: RescueDeviceOrderAttestationVariant) {
  track('Devices Attestation Agreed', { attestation_variant: variant });
}

export function trackDevicesOrderSubmitted(
  deviceCount: number,
  includeHardCases: boolean,
  variant?: RescueDeviceOrderAttestationVariant,
) {
  track('Devices Order Submitted', {
    device_count: deviceCount,
    include_hard_cases: includeHardCases,
    attestation_variant: variant,
  });
}

// ── Foster roster ──────────────────────────────────────────────────
//
// Same PII rule as the CSV import: the roster is a contact list, so never send a foster's name,
// email, phone, or address. Counts and stable slugs only.

/** Whether a placement reused a saved roster home or entered a new foster by hand. */
export type FosterSource = 'saved' | 'new';

/**
 * A home was saved to the roster from the roster's own Add button. Homes saved as a side effect of
 * a placement arrive via `Device Transfer to Foster Initiated`'s `save_to_roster` instead — they go
 * through a different mutation, so keeping them as separate events preserves that distinction.
 */
export function trackFosterHomeCreated() {
  track('Foster Home Created');
}

/** `placementCount` is the home's lifetime total, so archives can be split from never-used ones. */
export function trackFosterHomeArchived(placementCount: number) {
  track('Foster Home Archived', { placement_count: placementCount });
}

/** Once per visit to the Fosters tab, after the roster loads. `homeCount` sizes the roster. */
export function trackFosterRosterViewed(homeCount: number) {
  track('Foster Roster Viewed', { home_count: homeCount });
}

// ── Pet View ───────────────────────────────────────────────────────

export function trackPetViewed(hasChip: boolean, hasDevice: boolean, petStatus: string, species?: string) {
  track('Pet Viewed', {
    has_chip: hasChip,
    has_device: hasDevice,
    pet_status: petStatus,
    species,
  });
}

export function trackPetCreated(species?: string) {
  track('Pet Created', { species });
}

export function trackPetEdited(species?: string) {
  track('Pet Edited', { species });
}

export function trackPetRemoved(hadChip: boolean, hadDevice: boolean) {
  track('Pet Removed', { had_chip: hadChip, had_device: hadDevice });
}

export function trackPetChipMenuOpened() {
  track('Pet Chip Menu Opened');
}

export function trackPetDeviceMenuOpened() {
  track('Pet Device Menu Opened');
}

export function trackPetDeviceLinkClicked() {
  track('Pet Device Link Clicked');
}

export function trackPetChipLinkClicked() {
  track('Pet Chip Link Clicked');
}

// ── GWA: Enrollment funnel ─────────────────────────────────────────

export function trackGwaSetupNowClicked() {
  track('GWA Setup Now Clicked');
}

export function trackGwaEnrollmentModalViewed() {
  track('GWA Enrollment Modal Viewed');
}

export function trackGwaEnrolled(promoCodeAlias: string) {
  track('GWA Enrolled', { promo_code_alias: promoCodeAlias });
}

export function trackGwaEnrollmentFailed(
  errorMessage: string,
  failureType: 'client' | 'server',
  promoCodeAlias: string,
) {
  track('GWA Enrollment Failed', {
    error_message: errorMessage,
    failure_type: failureType,
    promo_code_alias: promoCodeAlias,
  });
}

export function trackGwaEnrollmentModalDismissed(step: number) {
  track('GWA Enrollment Modal Dismissed', { step });
}

// ── GWA: Dashboard ─────────────────────────────────────────────────

export function trackGwaDashboardViewed(totalRedemptions: number, monthRedemptions: number) {
  track('GWA Dashboard Viewed', {
    total_redemptions: totalRedemptions,
    month_redemptions: monthRedemptions,
  });
}

export function trackGwaLinkCopied() {
  track('GWA Link Copied');
}

export function trackGwaCodeCopied() {
  track('GWA Code Copied');
}

export function trackGwaBlurbCopied(blurbType: 'email' | 'print') {
  track('GWA Blurb Copied', { blurb_type: blurbType });
}

export function trackGwaPostcardsOrderSubmitted(quantity: number) {
  track('GWA Postcards Order Submitted', { quantity });
}

// ── Integrations ───────────────────────────────────────────────────

export function trackShelterLuvSetupStarted() {
  track('ShelterLuv Setup Started');
}

export function trackShelterLuvStepCompleted(stepNumber: number, stepName: string) {
  track('ShelterLuv Step Completed', { step_number: stepNumber, step_name: stepName });
}

export function trackShelterLuvConnected(chipsImported: number) {
  track('ShelterLuv Connected', { chips_imported: chipsImported });
}

export function trackShelterLuvValidationFailed() {
  track('ShelterLuv Validation Failed');
}

export function trackShelterLuvDisconnected() {
  track('ShelterLuv Disconnected');
}

export function trackShelterLuvEmailUpdatesToggled(optIn: boolean) {
  track('ShelterLuv Email Updates Toggled', { opt_in: optIn });
}

// ── CSV Import wizard ──────────────────────────────────────────────
//
// Per-event PII rule: never include row content (names, emails, phones,
// addresses, chip serials). Counts, codes, and step numbers only.

export function trackImportWizardOpened() {
  track('CSV Import Wizard Opened');
}

export function trackImportTemplateDownloaded() {
  track('CSV Import Template Downloaded');
}

export interface ImportFileParsedProps {
  row_count: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  file_size_bytes: number;
  /** Aggregate counts of which validation codes failed, keyed by code. */
  error_codes?: Record<string, number>;
}

export function trackImportFileParsed(props: ImportFileParsedProps) {
  track('CSV Import File Parsed', { ...props });
}

export function trackImportFileRejected(reason: string) {
  // `reason` is a short error class (e.g. "size_exceeded", "missing_header",
  // "malformed_csv"), not a free-form message — keeps the event clean for
  // aggregation in Mixpanel/Segment.
  track('CSV Import File Rejected', { reason });
}

/**
 * Fires when the CSV has been uploaded to S3 successfully. A backend
 * worker performs the actual import asynchronously, so this event is
 * "upload received", not "rows committed". `claim_count` is the subset
 * of rows whose chip the rescue would be claiming (computed
 * client-side from the eligibility lookup).
 */
export function trackImportPublished(props: { row_count: number; claim_count: number }) {
  track('CSV Import Published', { ...props });
}

/**
 * Fires when the upload flow errored — either the presigned-URL fetch
 * (network, auth, GraphQL error) or the S3 PUT itself (non-2xx
 * status). `reason` is a category code, not the raw message — backend
 * error text may include identifiers.
 */
export function trackImportFailed(reason: string) {
  track('CSV Import Failed', { reason });
}

export function trackImportAbandoned(step: 1 | 2 | 3, hasParsed: boolean) {
  // `step` matches the wizard step the user was on when they closed.
  // `hasParsed` distinguishes "closed before uploading" from "closed
  // after seeing the verify table".
  track('CSV Import Abandoned', { step, has_parsed: hasParsed });
}

/**
 * Fires when the backend worker reports the import finished successfully.
 * Counts come straight from `rescueCsvImportProgress` and are null until
 * the worker populates them. Counts-only — no row content.
 */
export function trackImportProcessingCompleted(props: {
  total_rows: number | null;
  final_processed: number | null;
  final_failed: number | null;
}) {
  track('CSV Import Processing Completed', { ...props });
}

/**
 * Fires when the worker job ends without success — either it FAILED or the
 * `rescueStartImport` hand-off itself errored. `reason` is a short category
 * ("failed", "start_error"), never the raw `terminalError` text (which may
 * carry identifiers).
 */
export function trackImportProcessingFailed(reason: string) {
  track('CSV Import Processing Failed', { reason });
}

// ── Manage Account ─────────────────────────────────────────────────
//
// Section names are stable slugs ("organization", "primary_contact",
// "address", "shipping_address") — never field values (PII).

export type AccountSection = 'organization' | 'primary_contact' | 'address' | 'shipping_address';

export function trackAccountMenuOpened() {
  track('Account Menu Opened');
}

export function trackAccountEditOpened(section: AccountSection) {
  track('Account Edit Opened', { section });
}

export function trackAccountSectionSaved(section: AccountSection) {
  track('Account Section Saved', { section });
}

export function trackAccountSectionSaveFailed(section: AccountSection) {
  track('Account Section Save Failed', { section });
}

export function trackAccount501c3Uploaded(fileSizeBytes: number) {
  track('Account 501c3 Uploaded', { file_size_bytes: fileSizeBytes });
}

/** Client-side validation refused the file before any upload was attempted. */
export function trackAccount501c3Rejected(reason: 'not_pdf' | 'size_exceeded') {
  track('Account 501c3 Rejected', { reason });
}

/**
 * The upload itself failed — the presigned-URL query, the S3 PUT, or the submit
 * mutation. `reason` is a coarse bucket (`empty_upload_url`, `s3_put_status_403`,
 * `network_error`, `forbidden`, `unknown`), never a raw error string.
 */
export function trackAccount501c3UploadFailed(reason: string) {
  track('Account 501c3 Upload Failed', { reason });
}

// ── Help Center ────────────────────────────────────────────────────

export function trackHelpArticleViewed(category: string, title: string) {
  track('Help Article Viewed', { article_category: category, article_title: title });
}

export function trackHelpSearched(query: string, resultsCount: number) {
  track('Help Searched', { query, results_count: resultsCount });
}

export function trackHelpContactClicked() {
  track('Help Contact Clicked');
}
